import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button, Card, Container, Form, Table } from "react-bootstrap";
import axios from "axios";
import { baseURL } from "../../API/API";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import Header from "../../Components/common/Header";
import Footer from "../../Components/common/Footer";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCopy, faUpload } from "@fortawesome/free-solid-svg-icons";
import { getCoursePackageTypeLabel } from "../../utils/packageLabels";

export default function CoursePackageInvoice() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const [pkg, setPkg] = useState(null);
  const [settings, setSettings] = useState({ payment_number: "", admin_instapay: "", instructions: "" });
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("Vodafone Cash");
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [settingsRes, packageRes] = await Promise.all([
          axios.get(`${baseURL}/learning/settings`),
          axios.get(`${baseURL}/learning/packages/detail/${packageId}`),
        ]);
        setSettings(settingsRes.data?.data || {});
        setPkg(packageRes.data?.data);
      } catch (err) {
        toast.error("فشل تحميل الفاتورة");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [packageId]);

  if (loading) return <><Header /><div className="text-center py-5 text-muted"><h3>جاري تحميل الفاتورة...</h3></div><Footer /></>;
  if (!pkg) return <><Header /><div className="text-center py-5 text-muted"><h3>الباقة غير موجودة</h3></div><Footer /></>;

  const finalPrice = Math.max(Number(pkg.price) - Number(pkg.discount), 0);
  const activeVodafoneCash = pkg.vodafone_cash || settings.payment_number;
  const activeInstaPay = pkg.instapay || settings.admin_instapay;
  const activePaymentTarget = paymentMethod === "Vodafone Cash" ? activeVodafoneCash : activeInstaPay;
  const packageLabel = getCoursePackageTypeLabel(pkg);

  const copy = (text) => {
    navigator.clipboard.writeText(text || "");
    toast.success("تم النسخ");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!screenshot) {
      toast.warning("يرجى رفع إثبات التحويل");
      return;
    }
    setSubmitting(true);
    const formData = new FormData();
    formData.append("amount", finalPrice);
    formData.append("method", paymentMethod);
    formData.append("screenshot", screenshot);
    formData.append("course_package_id", pkg.id);
    try {
      await axiosPrivate.post(`${baseURL}/payment/submit-credit-request`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم إرسال طلب الدفع. بعد موافقة الإدارة ستفتح كل شهور الباقة.");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "فشل إرسال طلب الدفع");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div dir="rtl" style={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <Header />
      <Container className="my-5" style={{ maxWidth: 820 }}>
        <Link to={`/packages/${pkg.id}`} className="text-decoration-none d-inline-flex align-items-center gap-2 mb-4 text-secondary"><FontAwesomeIcon icon={faArrowRight} /> العودة للباقة</Link>
        <Card className="border-0 shadow rounded-4 p-4 text-center bg-white mb-4">
          <h2 className="fw-bold mb-4">فاتورة الاشتراك في {packageLabel}</h2>
          <Table bordered className="align-middle text-center">
            <thead style={{ backgroundColor: "#f3f4f6" }}><tr><th>الباقة</th><th>المدرس</th><th>الخصم</th><th>الإجمالي</th></tr></thead>
            <tbody><tr className="fs-5"><td className="fw-bold">{pkg.name}</td><td>{pkg.teacher_name}</td><td className="text-danger fw-bold">{Number(pkg.discount).toFixed(0)} ج.م</td><td className="text-primary fw-bold fs-4">{finalPrice.toFixed(0)} ج.م</td></tr></tbody>
          </Table>
          <div className="bg-light p-4 rounded-4 text-end mt-3">
            <h4 className="fw-bold mb-4">بيانات الدفع</h4>
            <div className="d-flex justify-content-center gap-3 mb-4 flex-wrap">
              <Button variant={paymentMethod === "Vodafone Cash" ? "danger" : "outline-danger"} onClick={() => setPaymentMethod("Vodafone Cash")} disabled={!activeVodafoneCash}>فودافون كاش</Button>
              <Button variant={paymentMethod === "InstaPay" ? "success" : "outline-success"} onClick={() => setPaymentMethod("InstaPay")} disabled={!activeInstaPay}>انستا باي</Button>
            </div>
            <div className="border rounded-3 p-3 text-center bg-white mb-3">
              <div className="text-muted mb-2">حوّل مبلغ {finalPrice.toFixed(0)} ج.م إلى:</div>
              <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap"><span className="fs-3 fw-bold">{activePaymentTarget || "غير متوفر"}</span><Button size="sm" variant="outline-secondary" onClick={() => copy(activePaymentTarget)}><FontAwesomeIcon icon={faCopy} /> نسخ</Button></div>
            </div>
          </div>
        </Card>
        <Card className="border-0 shadow rounded-4 p-4 bg-white">
          <Form onSubmit={submit} className="text-end">
            <h4 className="fw-bold mb-4">رفع إثبات التحويل</h4>
            <Form.Group className="mb-4">
              <Form.Control type="file" accept="image/*" onChange={(e) => setScreenshot(e.target.files[0])} required />
              {screenshot && <div className="mt-2 text-success fw-bold">تم اختيار: {screenshot.name}</div>}
            </Form.Group>
            <Button type="submit" disabled={submitting || !screenshot} className="w-100 rounded-pill py-3 fw-bold fs-5" style={{ backgroundColor: "var(--brand-300)", borderColor: "var(--brand-300)" }}><FontAwesomeIcon icon={faUpload} /> {submitting ? "جاري الإرسال..." : "ارسل طلب الدفع"}</Button>
          </Form>
        </Card>
      </Container>
      <Footer />
    </div>
  );
}